import type { CollectionReference, DocumentData, DocumentSnapshot } from 'firebase/firestore'
import { collection, doc, limit, orderBy, query, type QuerySnapshot } from 'firebase/firestore'

import { getFirebaseFirestore } from '@/lib/firebase/app'
import { callable } from '@/lib/firebase/callable'
import { createReasonGuard } from '@/lib/firebase/errors'
import { type Subscription, subscribeCollection, subscribeDoc } from '@/lib/firebase/subscribe'
import type { BrandScope } from '@/repositories/brand'
import {
  type SetupScope,
  asOneOf,
  asRecords,
  asNumber,
  asStringOrNull,
  isRecord,
  subscribeLatestRun,
  toRunEntry,
  toSetupRun,
} from '@/repositories/setup-docs'
import {
  type BrandCandidate,
  COMPETITOR_ANALYSIS_ERROR_CODES,
  COMPETITOR_CANDIDATE_GROUPS,
  COMPETITOR_DISCOVERY_ERROR_CODES,
  COMPETITOR_DISCOVERY_STAGES,
  type CompetitorCandidate,
  type CompetitorCandidateRun,
  type CompetitorChoice,
  type CompetitorNamedOverlap,
  DISCOVERY_LIST_IDS,
  type DiscoveryListEntry,
  type DiscoveryListId,
  type KnownCompetitor,
  type Competitor,
  type CompetitorLastError,
  type CompetitorOverlap,
  type CompetitorTopicStanding,
  type CompetitorPositioning,
  type CompetitorRivalPage,
  type CompetitorReport,
  type CompetitorReportEntity,
  type CompetitorSearchOverlap,
  type CompetitorStatus,
  type CompetitorWinnableKeyword,
  type SearchMarket,
} from '@/types/competition'
import { SEARCH_INTENTS, type SearchIntent } from '@/types/search-intent'

function brandDocRef(scope: BrandScope) {
  return doc(getFirebaseFirestore(), 'workspaces', scope.workspaceId, 'brands', scope.brandId)
}

function competitorReportDocRef(scope: BrandScope) {
  return doc(brandDocRef(scope), 'competitorReport', 'current')
}

function competitorReportHistoryRef(scope: BrandScope): CollectionReference<DocumentData> {
  return collection(brandDocRef(scope), 'competitorReportHistory')
}

function competitorsCollectionRef(scope: BrandScope): CollectionReference<DocumentData> {
  return collection(brandDocRef(scope), 'competitors')
}

const COMPETITOR_STATUSES: readonly CompetitorStatus[] = ['never-analysed', 'ok', 'failed']

function asString(value: unknown): string | undefined {
  return typeof value === 'string' && value.trim() !== '' ? value : undefined
}

function asIso(value: unknown): string | undefined {
  return typeof value === 'string' && value ? value : undefined
}

function asStatus(value: unknown): CompetitorStatus {
  return typeof value === 'string' && (COMPETITOR_STATUSES as readonly string[]).includes(value)
    ? (value as CompetitorStatus)
    : 'never-analysed'
}

function toCompetitorLastError(value: unknown): CompetitorLastError | undefined {
  if (typeof value !== 'object' || value === null) return undefined
  const record = value as DocumentData
  const at = asIso(record.at)
  if (typeof record.code !== 'string' || at === undefined) return undefined
  return {
    code: asOneOf(record.code, COMPETITOR_ANALYSIS_ERROR_CODES, 'competitor_analysis_failed'),
    at,
  }
}

function toCompetitor(id: string, data: DocumentData | undefined): Competitor | undefined {
  if (!data) return undefined
  const name = asString(data.name)
  const domain = asString(data.domain)
  const websiteUrl = asString(data.websiteUrl)
  if (name === undefined || domain === undefined || websiteUrl === undefined) return undefined

  const lastError = toCompetitorLastError(data.lastError)

  return {
    competitorId: id,
    name,
    domain,
    websiteUrl,
    reason: asString(data.reason) ?? null,
    addedAt: asIso(data.addedAt) ?? null,
    status: asStatus(data.status),
    ...(asIso(data.analyzedAt) !== undefined ? { analyzedAt: asIso(data.analyzedAt) } : {}),
    ...(asIso(data.lastAttemptAt) !== undefined
      ? { lastAttemptAt: asIso(data.lastAttemptAt) }
      : {}),
    ...(lastError ? { lastError } : {}),
  }
}

function toCompetitors(snapshot: QuerySnapshot<DocumentData>): Competitor[] {
  return snapshot.docs
    .map((docSnapshot) => toCompetitor(docSnapshot.id, docSnapshot.data()))
    .filter((competitor): competitor is Competitor => competitor !== undefined)
}

export function subscribeCompetitors(scope: BrandScope): Subscription<Competitor[]> {
  return subscribeCollection(competitorsCollectionRef(scope), toCompetitors)
}

function asNumberOrNull(value: unknown): number | null | undefined {
  if (typeof value === 'number') return value
  if (value === null) return null
  return undefined
}

function toCompetitorReportEntity(value: unknown): CompetitorReportEntity | undefined {
  if (typeof value !== 'object' || value === null) return undefined
  const record = value as DocumentData
  const domain = asString(record.domain)
  const estimatedTraffic = asNumberOrNull(record.estimatedTraffic)
  const rankingKeywordCount = asNumberOrNull(record.rankingKeywordCount)
  if (
    domain === undefined ||
    estimatedTraffic === undefined ||
    rankingKeywordCount === undefined ||
    typeof record.shareOfSearch !== 'number' ||
    typeof record.aiOverviewCitationCount !== 'number'
  ) {
    return undefined
  }
  const competitorId = typeof record.competitorId === 'string' ? record.competitorId : null
  return {
    competitorId,
    domain,
    estimatedTraffic,
    rankingKeywordCount,
    shareOfSearch: record.shareOfSearch,
    aiOverviewCitationCount: record.aiOverviewCitationCount,
  }
}

function toCompetitorOverlap(value: unknown): CompetitorOverlap | undefined {
  if (typeof value !== 'object' || value === null) return undefined
  const record = value as DocumentData
  const competitorId = asString(record.competitorId)
  if (
    competitorId === undefined ||
    typeof record.sharedKeywordCount !== 'number' ||
    typeof record.theirsOnlyKeywordCount !== 'number' ||
    typeof record.oursOnlyKeywordCount !== 'number'
  ) {
    return undefined
  }
  return {
    competitorId,
    sharedKeywordCount: record.sharedKeywordCount,
    theirsOnlyKeywordCount: record.theirsOnlyKeywordCount,
    oursOnlyKeywordCount: record.oursOnlyKeywordCount,
  }
}

function toCompetitorPositioning(value: unknown): CompetitorPositioning | undefined {
  if (typeof value !== 'object' || value === null) return undefined
  const record = value as DocumentData
  const competitorId = asString(record.competitorId)
  const line = asString(record.line)
  return competitorId === undefined || line === undefined ? undefined : { competitorId, line }
}

function toCompetitorTopicStanding(value: unknown): CompetitorTopicStanding | undefined {
  if (typeof value !== 'object' || value === null) return undefined
  const record = value as DocumentData
  const topicId = asString(record.topicId)
  const averagePosition = asNumberOrNull(record.averagePosition)
  if (
    topicId === undefined ||
    averagePosition === undefined ||
    typeof record.keywordCount !== 'number'
  ) {
    return undefined
  }
  const competitorId = typeof record.competitorId === 'string' ? record.competitorId : null
  return { topicId, competitorId, keywordCount: record.keywordCount, averagePosition }
}

function asFiniteOrNull(value: unknown): number | null {
  return typeof value === 'number' && Number.isFinite(value) ? value : null
}

function asIntentOrNull(value: unknown): SearchIntent | null {
  return typeof value === 'string' && (SEARCH_INTENTS as readonly string[]).includes(value)
    ? (value as SearchIntent)
    : null
}

export function toSearchMarket(value: unknown): SearchMarket | undefined {
  if (!isRecord(value)) return undefined
  const languageCode = asString(value.languageCode)
  if (typeof value.locationCode !== 'number' || languageCode === undefined) return undefined
  return { locationCode: value.locationCode, languageCode }
}

function toWinnableKeyword(value: unknown): CompetitorWinnableKeyword | undefined {
  if (!isRecord(value)) return undefined
  const keyword = asString(value.keyword)
  if (keyword === undefined) return undefined
  return {
    keyword,
    volume: asFiniteOrNull(value.volume),
    difficulty: asFiniteOrNull(value.difficulty),
    intent: asIntentOrNull(value.intent),
    bestRivalPosition: asFiniteOrNull(value.bestRivalPosition),
    bestRivalUrl: asStringOrNull(value.bestRivalUrl),
    competitorId: asStringOrNull(value.competitorId),
  }
}

function toRivalPage(value: unknown): CompetitorRivalPage | undefined {
  if (!isRecord(value)) return undefined
  const competitorId = asString(value.competitorId)
  const url = asString(value.url)
  if (competitorId === undefined || url === undefined) return undefined
  return { competitorId, url, etv: asFiniteOrNull(value.etv), count: asFiniteOrNull(value.count) }
}

function toArray<T>(value: unknown, decode: (item: unknown) => T | undefined): T[] {
  return Array.isArray(value)
    ? value.map(decode).filter((item): item is T => item !== undefined)
    : []
}

/** Maps a `competitorReport/current` body to a `CompetitorReport`; `undefined` when a required field is missing. */
function toCompetitorReport(data: DocumentData): CompetitorReport | undefined {
  const generatedAt = asIso(data.generatedAt)
  if (typeof data.comparisonSetSize !== 'number' || generatedAt === undefined) return undefined
  const competitorsChangedAt = asIso(data.competitorsChangedAt)
  const market = toSearchMarket(data.market)
  const snapshotFetchedAt = asIso(data.snapshotFetchedAt)

  return {
    comparisonSetSize: data.comparisonSetSize,
    entities: toArray(data.entities, toCompetitorReportEntity),
    overlaps: toArray(data.overlaps, toCompetitorOverlap),
    positioning: toArray(data.positioning, toCompetitorPositioning),
    topicStandings: toArray(data.topicStandings, toCompetitorTopicStanding),
    generatedAt,
    ...(competitorsChangedAt !== undefined ? { competitorsChangedAt } : {}),
    gaps: asNumber(data.gaps),
    wins: asNumber(data.wins),
    contested: asNumber(data.contested),
    winnable: toArray(data.winnable, toWinnableKeyword),
    rivalPages: toArray(data.rivalPages, toRivalPage),
    ...(market !== undefined ? { market } : {}),
    ...(snapshotFetchedAt !== undefined ? { snapshotFetchedAt } : {}),
  }
}

function toCompetitorReportOrUndefined(
  snapshot: DocumentSnapshot<DocumentData>,
): CompetitorReport | undefined {
  const data = snapshot.data()
  return data ? toCompetitorReport(data) : undefined
}

/** Live view of `competitorReport/current`; no comparison yet is a normal state. */
export function subscribeCompetitorReport(scope: BrandScope): Subscription<CompetitorReport> {
  return subscribeDoc(competitorReportDocRef(scope), toCompetitorReportOrUndefined)
}

const COMPETITOR_REPORT_HISTORY_LIMIT = 24

function toCompetitorReports(snapshot: QuerySnapshot<DocumentData>): CompetitorReport[] {
  return snapshot.docs
    .map((docSnapshot) => toCompetitorReport(docSnapshot.data()))
    .filter((report): report is CompetitorReport => report !== undefined)
}

export function subscribeCompetitorReportHistory(
  scope: BrandScope,
): Subscription<CompetitorReport[]> {
  return subscribeCollection(
    query(
      competitorReportHistoryRef(scope),
      orderBy('generatedAt', 'desc'),
      limit(COMPETITOR_REPORT_HISTORY_LIMIT),
    ),
    toCompetitorReports,
  )
}

interface RawAddCompetitorInput {
  name: string
  websiteUrl: string
}

interface RawRemoveCompetitorInput {
  competitorId: string
}

const addCompetitorCallable = callable<RawAddCompetitorInput, void>('addCompetitor')
const removeCompetitorCallable = callable<RawRemoveCompetitorInput, void>('removeCompetitor')
const generateCompetitorAnalysisCallable = callable<Record<string, never>, DocumentData>(
  'generateCompetitorAnalysis',
)

export async function addCompetitor(input: RawAddCompetitorInput): Promise<void> {
  await addCompetitorCallable(input)
}

export async function removeCompetitor(input: RawRemoveCompetitorInput): Promise<void> {
  await removeCompetitorCallable(input)
}

const GENERATE_ANALYSIS_REASONS = [
  'brand_domain_unknown',
  'brand_market_undecided',
  'search_snapshot_missing',
] as const

export const isGenerateAnalysisPreconditionReason = createReasonGuard(GENERATE_ANALYSIS_REASONS)

/** Generates (or returns the fresh cached) comparison for the tracked set; prefer the subscriptions as the read path. */
export async function generateCompetitorAnalysis(): Promise<CompetitorReport | undefined> {
  const raw = await generateCompetitorAnalysisCallable({})
  return toCompetitorReport(raw)
}

function isDiscoveryListId(value: unknown): value is DiscoveryListId {
  return typeof value === 'string' && (DISCOVERY_LIST_IDS as readonly string[]).includes(value)
}

function toDiscoveryListEntry(entry: DocumentData): DiscoveryListEntry | undefined {
  if (!isDiscoveryListId(entry.id)) return undefined
  return {
    ...toRunEntry(entry),
    id: entry.id,
    found: asNumber(entry.found),
  }
}

function toBrandCandidate(record: DocumentData): BrandCandidate | undefined {
  const name = asString(record.name)
  const domain = asString(record.domain)
  const websiteUrl = asString(record.websiteUrl)
  if (name === undefined || domain === undefined || websiteUrl === undefined) return undefined
  return { name, domain, websiteUrl }
}

function toSearchOverlap(value: unknown): CompetitorSearchOverlap | null {
  if (!isRecord(value) || typeof value.intersections !== 'number') return null
  return { intersections: value.intersections, sharedTraffic: asFiniteOrNull(value.sharedTraffic) }
}

function toNamedOverlap(record: DocumentData): CompetitorNamedOverlap | undefined {
  const of = asString(record.of)
  const overlap = toSearchOverlap(record)
  if (of === undefined || overlap === null) return undefined
  return { of, ...overlap }
}

function toCompetitorCandidate(record: DocumentData): CompetitorCandidate | undefined {
  const candidate = toBrandCandidate(record)
  if (candidate === undefined) return undefined
  const lists = Array.isArray(record.lists) ? record.lists.filter(isDiscoveryListId) : []
  return {
    ...candidate,
    lists,
    group: asOneOf(record.group, COMPETITOR_CANDIDATE_GROUPS, 'direct'),
    reason: asString(record.reason) ?? null,
    searchOverlap: toSearchOverlap(record.searchOverlap),
    namedOverlap: asRecords(record.namedOverlap)
      .map(toNamedOverlap)
      .filter((overlap): overlap is CompetitorNamedOverlap => overlap !== undefined),
  }
}

function toCompetitorCandidateRun(
  snapshot: DocumentSnapshot<DocumentData>,
): CompetitorCandidateRun | undefined {
  const data = snapshot.data()
  if (!data) return undefined
  const { errorCode, ...run } = toSetupRun(
    snapshot.id,
    data,
    COMPETITOR_DISCOVERY_STAGES,
    toDiscoveryListEntry,
  )
  return {
    ...run,
    ...(errorCode !== undefined
      ? {
          errorCode: asOneOf(
            errorCode,
            COMPETITOR_DISCOVERY_ERROR_CODES,
            'competitor_discovery_failed',
          ),
        }
      : {}),
    candidates: asRecords(data.candidates)
      .map(toCompetitorCandidate)
      .filter((candidate): candidate is CompetitorCandidate => candidate !== undefined),
    startedByUserId: typeof data.startedByUserId === 'string' ? data.startedByUserId : '',
  }
}

export function subscribeLatestCompetitorCandidateRun(
  scope: SetupScope,
): Subscription<CompetitorCandidateRun | null> {
  return subscribeLatestRun(scope, 'competitorCandidateRuns', toCompetitorCandidateRun)
}

export interface DiscoverCompetitorsResult {
  runId: string
}

export const isCompetitorDiscoveryRunInProgressReason = createReasonGuard([
  'competitor_discovery_run_in_progress',
] as const)

const discoverCompetitorsCallable = callable<
  { knownCompetitors: readonly KnownCompetitor[] },
  DiscoverCompetitorsResult
>('discoverCompetitors')

/** Tracks the named companies (at most four) and starts a discovery that looks for more. */
export async function discoverCompetitors(
  knownCompetitors: readonly KnownCompetitor[],
): Promise<DiscoverCompetitorsResult> {
  return discoverCompetitorsCallable({ knownCompetitors })
}

export const BRAND_SEARCH_QUERY_MAX_LENGTH = 100

const searchBrandCandidatesCallable = callable<{ query: string }, { results: DocumentData[] }>(
  'searchBrandCandidates',
)

/** Finds up to five companies by typed name or website. */
export async function searchBrandCandidates(query: string): Promise<BrandCandidate[]> {
  const raw = await searchBrandCandidatesCallable({ query })
  return asRecords(raw.results)
    .map(toBrandCandidate)
    .filter((candidate): candidate is BrandCandidate => candidate !== undefined)
}

export interface ConfirmCompetitorsResult {
  added: number
}

const confirmCompetitorsCallable = callable<
  { candidates: readonly CompetitorChoice[] },
  ConfirmCompetitorsResult
>('confirmCompetitors')

/** Replaces the tracked set with the kept competitors (one to four), stamps the confirm and starts the search snapshot. */
export async function confirmCompetitors(
  candidates: readonly CompetitorChoice[],
): Promise<ConfirmCompetitorsResult> {
  return confirmCompetitorsCallable({ candidates })
}
